import React, {useState, useEffect, useContext} from 'react';
import {Card, Button, Typography, Select, Row, Col, Alert, Input, Spin} from 'antd';
import {SearchOutlined, WarningOutlined, ReloadOutlined} from '@ant-design/icons';
import {internalApi} from '../../common/axios';
import {handleResponse} from '../../shared/utils/net';
import toast from 'react-hot-toast';
import {AuthContext} from '../../shared/contexts/authContext';
import {ENV} from '../../shared/utils/enum';

const {Title, Text} = Typography;

const LogStreamPage = () => {
    const {currentUser} = useContext(AuthContext);
    const [files, setFiles] = useState([]);
    const [selectedFile, setSelectedFile] = useState(null);
    const [keyword, setKeyword] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [loadingFiles, setLoadingFiles] = useState(false);
    const [searching, setSearching] = useState(false);

    const isQAServer = currentUser?.target === ENV.QA;

    // 파일 리스트 로드
    const loadFiles = async () => {
        setLoadingFiles(true);
        try {
            const resp = await internalApi.get('/log_stream/files');
            handleResponse(resp,
                (data) => {
                    setFiles(data.files || []);
                    setLoadingFiles(false);
                },
                (msg) => {
                    toast.error(`파일 로드 실패: ${msg}`);
                    setLoadingFiles(false);
                }
            );
        } catch (error) {
            toast.error('파일 로드 중 오류가 발생했습니다.');
            setLoadingFiles(false);
        }
    };

    // 키워드 검색
    const searchLogs = async () => {
        if (!selectedFile || !keyword) {
            toast.error('파일과 키워드를 모두 입력해주세요.');
            return;
        }

        setSearching(true);
        setSearchResults([]);

        try {
            const resp = await internalApi.post('/log_stream/search', {
                fileName: selectedFile,
                keyword
            });
            handleResponse(resp,
                (data) => {
                    setSearchResults(data.matches || []);
                    if (data.count === 0) {
                        toast.success('검색 결과가 없습니다.');
                    } else {
                        toast.success(`${data.count}개의 결과를 찾았습니다.`);
                    }
                    setSearching(false);
                },
                (msg) => {
                    toast.error(`검색 실패: ${msg}`);
                    setSearching(false);
                }
            );
        } catch (error) {
            toast.error('검색 중 오류가 발생했습니다.');
            setSearching(false);
        }
    };

    // 컴포넌트 마운트 시 파일 리스트 로드
    useEffect(() => {
        if (isQAServer) {
            loadFiles();
        }
    }, [isQAServer]);

    return (
        <div style={{maxWidth: 800, margin: '40px auto', padding: '20px'}}>
            <Card>
                <div style={{textAlign: 'center', marginBottom: '40px'}}>
                    <div style={{fontSize: '48px', marginBottom: '16px'}}>🔍</div>
                    <Title level={2}>일본 QA 서버 로그 검색</Title>
                    <Text style={{color: '#666'}}>
                        로그 파일에서 키워드를 검색할 수 있습니다
                    </Text>
                </div>

                {!isQAServer && (
                    <Alert
                        message="접근 제한"
                        description="이 기능은 QA 서버에서만 사용할 수 있습니다. 다른 서버에서는 제한됩니다."
                        type="warning"
                        icon={<WarningOutlined/>}
                        showIcon
                        style={{marginBottom: '24px'}}
                    />
                )}

                {isQAServer && (
                    <>
                        <div style={{marginBottom: '24px'}}>
                            <Typography.Title level={4}>로그 파일 선택</Typography.Title>
                            <Row gutter={16} align="middle">
                                <Col flex="auto">
                                    <Select
                                        showSearch
                                        placeholder="로그 파일을 선택하세요"
                                        style={{width: '100%'}}
                                        value={selectedFile}
                                        onChange={setSelectedFile}
                                        loading={loadingFiles}
                                        filterOption={(input, option) =>
                                            (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                        }
                                        options={files
                                        .sort((a, b) => b.localeCompare(a))
                                        .map(file => ({
                                            value: file,
                                            label: file
                                        }))}
                                    />
                                </Col>
                                <Col>
                                    <Button
                                        icon={<ReloadOutlined/>}
                                        onClick={loadFiles}
                                        disabled={loadingFiles}
                                        loading={loadingFiles}
                                        size="large"
                                        title="파일 목록 새로고침"
                                    />
                                </Col>
                            </Row>
                            {selectedFile && (
                                <div style={{
                                    marginTop: '8px',
                                    padding: '8px 12px',
                                    background: '#f0f0f0',
                                    borderRadius: '4px',
                                    fontSize: '12px',
                                    color: '#666'
                                }}>
                                    경로: ~/bin/logs/airbridge/{selectedFile}
                                </div>
                            )}
                        </div>

                        <div style={{marginBottom: '24px'}}>
                            <Typography.Title level={4}>키워드 검색</Typography.Title>
                            <Row gutter={16}>
                                <Col flex="auto">
                                    <Input
                                        placeholder="검색할 키워드를 입력하세요"
                                        value={keyword}
                                        onChange={(e) => setKeyword(e.target.value)}
                                        onPressEnter={searchLogs}
                                        size="large"
                                    />
                                </Col>
                                <Col>
                                    <Button
                                        type="primary"
                                        icon={<SearchOutlined/>}
                                        onClick={searchLogs}
                                        disabled={!selectedFile || !keyword || searching}
                                        loading={searching}
                                        size="large"
                                        style={{minWidth: '100px'}}
                                    >
                                        검색
                                    </Button>
                                </Col>
                            </Row>
                        </div>

                        {searching && (
                            <div style={{textAlign: 'center', padding: '40px'}}>
                                <Spin size="large" />
                                <div style={{marginTop: '16px', color: '#666'}}>검색 중...</div>
                            </div>
                        )}

                        {!searching && searchResults.length > 0 && (
                            <div>
                                <Typography.Title level={4}>
                                    검색 결과 ({searchResults.length}개)
                                </Typography.Title>
                                <div style={{
                                    background: '#000',
                                    color: '#00ff00',
                                    padding: '16px',
                                    borderRadius: '4px',
                                    fontFamily: 'monospace',
                                    fontSize: '12px',
                                    maxHeight: '500px',
                                    overflow: 'auto'
                                }}>
                                    {searchResults.map((line, index) => (
                                        <div key={index} style={{marginBottom: '4px'}}>
                                            {line}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </>
                )}


            </Card>
        </div>
    );
};

export default LogStreamPage;